// editors/markdoc/decorations/link.js
import { Decoration, WidgetType } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import { hiddenDecoration, shouldHideMarkers } from "../utils/decorationHelper"

// Interface for link details
class LinkDetails {
  constructor({ text, url, zettelId, fullRange }) {
    this.text = text || "Link";
    this.url = url || "";
    this.zettelId = zettelId || "";
    this.fullRange = fullRange || null;
  }
}

// Base WidgetType for link rendering
class LinkWidget extends WidgetType {
  constructor(text, href, isZettel = false) {
    super();
    this.text = text;
    this.href = href;
    this.isZettel = isZettel;
  }

  toDOM() {
    const a = document.createElement("a");
    a.href = this.href;
    a.textContent = this.isZettel ? `${this.text}` : this.text;
    if (!this.isZettel) {
      a.textContent = `${a.textContent} ↗`;
      a.target = "_blank";
    }
    if (this.isZettel) {
      a.setAttribute("data-action", "click->pane-stack#openPane");
    }
    a.className = "cm-markdoc-link";

    return a;
  }
}

// Creates a decoration for a link
function createLinkDecoration(text, href, isZettel = false) {
  return Decoration.widget({
    widget: new LinkWidget(text, href, isZettel),
  });
}

// Handler for standard Markdown links [text](url)
function getStandardLinkDetails(view, linkNode) {
  let url = "";
  let text = "";
  const tree = syntaxTree(view.state);
  if (!tree) {
    return new LinkDetails({ text: "Link", url: "#" });
  }

  const nodeText = view.state.doc.sliceString(linkNode.from, linkNode.to);

  tree.iterate({
    from: linkNode.from,
    to: linkNode.to,
    enter(child) {
      // Handle URL (destination)
      if (["URL", "LinkDestination"].includes(child.name)) {
        url = view.state.doc.sliceString(child.from, child.to);
      }
      // Handle link text
      if (["LinkText", "LinkLabel"].includes(child.name)) {
        text = view.state.doc.sliceString(child.from, child.to);
      }
    },
  });

  // Fallback: If text is empty, try extracting it from the node text
  if (!text) {
    const match = nodeText.match(/^\[(.+?)\]\(.+?\)$/);
    if (match) {
      text = match[1];
    }
  }

  return new LinkDetails({
    text: text || url || "Link",
    url: url || "#",
  });
}

// Handler for Zettel links [[text|zettel:id]]
function getZettelLinkDetails(view, linkNode) {
  const nodeText = view.state.doc.sliceString(linkNode.from - 1, linkNode.to + 1);
  const match = nodeText.match(/^\[\[(.+?)\|([a-zA-Z0-9]+)\]\]$/);

  if (!match) {
    return new LinkDetails({ text: "Link", zettelId: "" });
  }

  const text = match[1];
  const zettelId = match[2];
  const doc = view.state.doc;
  const fullRangeStart = linkNode.from - 1;
  const fullRangeEnd = linkNode.to + 1;
  const fullText = doc.sliceString(fullRangeStart, fullRangeEnd);
  const isWrappedInBrackets = fullText.startsWith("[") && fullText.endsWith("]");

  return new LinkDetails({
    text,
    zettelId,
    fullRange: isWrappedInBrackets
      ? { from: fullRangeStart, to: fullRangeEnd }
      : { from: linkNode.from, to: linkNode.to },
  });
}

// Determines link type and delegates to appropriate parser
function getLinkDetails(view, linkNode) {
  // Check text including potential outer brackets [[...]]
  const nodeText = view.state.doc.sliceString(linkNode.from - 1, linkNode.to + 1);
  const isZettelLink = nodeText.match(/^\[\[(.+?)\|([a-zA-Z0-9]+)\]\]$/);
  return isZettelLink
    ? getZettelLinkDetails(view, linkNode)
    : getStandardLinkDetails(view, linkNode);
}

// Main handler for both link types
export function decorateLink(node, view, widgets, mode) {
  if (node.name !== "Link") return true; // Continue processing other nodes

  const [cursor] = view.state.selection.ranges;

  if (shouldHideMarkers(view, node, mode)) {
    const details = getLinkDetails(view, node);
    const { text, url, zettelId, fullRange } = details;

    if (zettelId || (url && url !== "#")) {
      const href = zettelId || url;
      const isZettel = !!zettelId;
      const range = fullRange || { from: node.from, to: node.to };

      const decoration = createLinkDecoration(text, href, isZettel);

      widgets.push(createLinkDecoration(text, href, isZettel).range(range.from));
      widgets.push(hiddenDecoration(range.from, range.to));
    }
  }

  return false; // Prevent further processing of Link children
}
